import type { CalendarDate } from './types';

/** Matches the date-only `YYYY-MM-DD` wire shape. */
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse a `YYYY-MM-DD` logical date into its calendar parts.
 *
 * Deliberately **string arithmetic** — never `new Date(value)`. That parses the
 * string as UTC midnight, so reading it back through the local `getMonth()` /
 * `getDate()` lands on the previous day in every zone behind UTC. The whole
 * point of a logical date (ADR 024) is that it never zone-shifts, so it must
 * never round-trip through an instant.
 *
 * The inverse of {@link toLogicalDateString}. Use this wherever you need the
 * *parts* of a calendar date (calendar grids, month arithmetic, comparisons);
 * use `formatLogicalDate` when you just need it rendered for a human.
 *
 * @param value - a `YYYY-MM-DD` string, or '' / undefined when unset.
 * @returns the parts (`month` 1-12), or `null` for empty/malformed input.
 * @sideEffect none — pure.
 */
export function parseLogicalDate(value: string | null | undefined): CalendarDate | null {
    if (!value) return null;

    const match = DATE_ONLY_RE.exec(value);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);

    // Reject structurally-valid but non-existent days ('2026-02-30', '2026-13-01').
    // `new Date(Date.UTC(...))` is safe here: it's used purely as a calendar
    // oracle and read back with getUTC*, so no local-zone lens is ever applied.
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const probe = new Date(Date.UTC(year, month - 1, day));
    if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null;

    return { year, month, day };
}
